import React from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import Logo from '../Components/Logo';

import Loading from '../Components/Loading';

import styles from '../styles/Auth.module.css';
import stylesLogo from '../styles/Logo.module.css';
import modalStatus from '../styles/DynamicModal.module.css';

// Хук для проверки статуса подключения к серверу
const useDbStatus = () => {
  return useQuery({
    queryKey: ['dbStatus'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/health');
        if (response.status === 200 && response.data.status === 'success') {
            return 'connected';
        } else {
            throw new Error('Некорректный ответ от сервера');
        }
        } catch (err) {
        if (err.response) {
            // Сервер ответил с кодом ошибки
            throw new Error(err.response.data.message || 'Ошибка сервера');
        } else if (err.request) {
            // Запрос был отправлен, но сервер не ответил
            throw new Error('Сервер недоступен. Проверьте соединение');
        } else {
            // Что-то пошло не так при настройке запроса
            throw new Error('Ошибка при выполнении запроса');
        }
      }
    },
    retry: 2, // Количество повторных попыток в случае ошибки
    refetchOnWindowFocus: false, // Не обновлять запрос при смене вкладок
  });
};

// Хук для авторизации
const useLogin = () => {
  return useMutation({
    mutationFn: async ({ email, password }) => {
      const { data } = await axios.post('/api/login', {
        email,
        password,
      });
      return data;
    },
  });
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const { data: dbStatus, isLoading: isDbLoading, error: dbError } = useDbStatus();
  const { mutate: login, isLoading: isLoginLoading, error: loginError, isError: isLoginError } = useLogin();

  const handleSubmit = (e) => {
    e.preventDefault();
    login(
      { email, password },
      {
        onSuccess: (data) => {
          localStorage.setItem('token', data.token);


          const queryParams = new URLSearchParams(location.search);
          const redirectPath = queryParams.get('redirect') || '/';
          navigate(redirectPath);
        },
      }
    );
  };

  if (isDbLoading) {
    return (
      <Loading />
    );
  }

  if (dbError || dbStatus !== 'connected') {
    return (
      <span style={{ display: 'flex', flexDirection: 'column', padding: '10px', boxSizing: 'border-box' }}>
        <a>Система «Поток» в данный момент недоступна.</a>
        <a>Статус: {dbError ? dbError.message : dbStatus}</a>
      </span>
    );
  }

  return (
    <div className={styles.indexMain}>
      <div className={styles.indexForm}>
        <a style={{zIndex: '9999'}} className={stylesLogo.widthLimitation}><Logo to='/' position='fixed' /></a>
        <form className={styles.liningForm} onSubmit={handleSubmit}>
          <div className={styles.liningHeader}>
            <h1>Войдите, чтобы пользоваться журналом «Поток»</h1>
            <Link to="/" style={{ display: 'flex' }}>
              <img src="/img/icons/close-x.svg" className={styles.closeButton} />
            </Link>
          </div>
          <div className={styles.liningBody}>
            <a>Введите адрес электронной почты</a>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Введите e-mail"
              minLength="3"
              maxLength="100"
              required
            />
            <a>Введите пароль</a>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Введите пароль"
              minLength="4"
              maxLength="180"
              required
            />
            {isLoginError && (
              <a className={`${modalStatus.statusPanel} ${styles.red}`}>
                <img src="/img/icons/error-icon.svg" alt="Ошибка" />
                {loginError?.response?.data || 'Ошибка системы'}
              </a>
            )}
            <button className="button maxWidth" type="submit" disabled={isLoginLoading}>
              {isLoginLoading ? 'Загрузка...' : 'Войти'}
            </button>
            <a className={styles.or}>У вас нет учетной записи?</a>
            <Link to="/auth/registration" className={styles.goToAnAuth}>
              Быстрая регистрация
            </Link>
            <Link className={styles.labelForPolicy} to='/privacy-policy'>Продолжая, вы подтверждаете, что понимаете и соглашаетесь с Пользовательским соглашением и политикой конфиденциальности.</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
